import { joinWithGrammar } from "@/utils/array-utils";
import { DAYS, daysById } from "../utils/dates";

type Schedule = {
  dayOfWeek: string;
  amount: number;
};

type Day = (typeof DAYS)[number];

export const getScheduleDays = (schedules: Schedule[]) =>
  schedules
    .map((schedule) => {
      const day = daysById.get(schedule.dayOfWeek.toLowerCase());
      if (!day) throw new Error(`Unknown day: ${schedule.dayOfWeek}`);
      return {
        ...day,
        amount: schedule.amount,
      };
    })
    .sort((a, b) => a.index - b.index);

const groupScheduleDaysByAmount = (schedules: Schedule[]) => {
  // Filter out schedules with amount 0
  const nonZeroSchedules = schedules.filter((schedule) => schedule.amount > 0);

  const days = getScheduleDays(nonZeroSchedules);

  const daysByAmount = new Map<number, Day[]>();

  for (const day of days) {
    let group = daysByAmount.get(day.amount);
    if (!group) {
      group = [];
      daysByAmount.set(day.amount, group);
    }
    group.push(day);
  }

  return daysByAmount;
};

const isConnectedDays = (days: Day[]) => {
  if (!days.length) return false;
  const [{ index: firstDayIndex }] = days;
  let expectedIndex = firstDayIndex;
  for (const day of days) {
    if (day.index !== expectedIndex) return false;
    expectedIndex++;
  }
  return true;
};

export const formatScheduleLabel = (schedules: Schedule[]) => {
  const daysByAmount = groupScheduleDaysByAmount(schedules);
  if (!daysByAmount.size) return "";

  if (daysByAmount.size === 1) {
    const [[amount, days]] = daysByAmount;
    return `${amount} pr. dag - ${formatScheduleDays(days)}`;
  }

  return [...daysByAmount]
    .map(([amount, days]) => {
      const dayNames = days.map((day) => day.name);
      return `${amount} ${joinWithGrammar(dayNames)}`;
    })
    .join(", ");
};

export const formatScheduleDays = (days: (Day & { amount?: number })[]) => {
  const filteredDays = days.filter(
    (day) => day.amount === undefined || day.amount > 0,
  );
  if (!filteredDays.length) return "";

  if (filteredDays.length >= 3 && isConnectedDays(filteredDays)) {
    return `${filteredDays[0].name} til ${filteredDays[filteredDays.length - 1].name}`;
  }

  return joinWithGrammar(filteredDays.map((day) => day.name));
};
